import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Check, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import type { DomainRule, DomainRuleListResponse, LinkListResponse, ShortLink } from "../../shared/types";
import { api, jsonBody } from "../lib/api";
import { date } from "../lib/format";
import { PageHeader } from "../components/app-shell";
import { Button, Empty, ErrorState, Field, Loading, Tag, useToast } from "../components/ui";

export function ReviewPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [pattern, setPattern] = useState("");
  const [action, setAction] = useState<"allow" | "block">("allow");
  const [ruleNote, setRuleNote] = useState("");
  const [reviewPage, setReviewPage] = useState(1);
  const [rulePage, setRulePage] = useState(1);
  const reviewQuery = useQuery({ queryKey: ["review", reviewPage], queryFn: () => api<LinkListResponse>(`/api/review?page=${reviewPage}&pageSize=20`) });
  const rulesQuery = useQuery({ queryKey: ["domain-rules", rulePage], queryFn: () => api<DomainRuleListResponse>(`/api/domain-rules?page=${rulePage}&pageSize=20`) });

  const items = reviewQuery.data?.items ?? [];
  const selected = items.find((item) => item.id === selectedId) ?? items[0] ?? null;

  const reviewMutation = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      api<ShortLink>(`/api/review/${selected?.id ?? ""}`, { method: "POST", ...jsonBody({ decision, note }) }),
    async onSuccess(link) {
      setNote("");
      toast.push(link.status === "active" ? "短链已通过并生效" : "短链已拒绝");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["review"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["links"] }),
      ]);
    },
    onError: toast.fromError,
  });
  const createRuleMutation = useMutation({
    mutationFn: () => api<DomainRule>("/api/domain-rules", { method: "POST", ...jsonBody({ pattern, action, note: ruleNote }) }),
    async onSuccess() {
      setPattern("");
      setRuleNote("");
      toast.push("域名规则已添加");
      await queryClient.invalidateQueries({ queryKey: ["domain-rules"] });
    },
    onError: toast.fromError,
  });
  const deleteRuleMutation = useMutation({
    mutationFn: (id: string) => api<{ deleted: boolean }>(`/api/domain-rules/${id}`, { method: "DELETE" }),
    async onSuccess() {
      toast.push("域名规则已删除");
      await queryClient.invalidateQueries({ queryKey: ["domain-rules"] });
    },
    onError: toast.fromError,
  });

  return (
    <div className="page review-page">
      <PageHeader eyebrow="GOVERNANCE · 审核模式" title="待审队列" body="左侧处理具体链接，右侧维护可复用的域名规则。命中白名单的目标直接生效。" actions={<Tag tone="accent">{reviewQuery.data?.pagination.total ?? "—"} PENDING</Tag>} />
      <section className="review-layout">
        <div className="review-queue">
          <header className="panel-heading"><div><span className="eyebrow">QUEUE · OLDEST FIRST</span><h2>等待决定</h2></div><ShieldCheck size={20} /></header>
          {reviewQuery.isLoading ? <Loading /> : reviewQuery.error ? <ErrorState error={reviewQuery.error} retry={() => void reviewQuery.refetch()} /> : reviewQuery.data?.items.length ? (
            <div className="review-queue__items">
              {reviewQuery.data.items.map((link) => (
                <button key={link.id} type="button" className={selected?.id === link.id ? "review-item review-item--selected" : "review-item"} onClick={() => setSelectedId(link.id)}>
                  <span className="eyebrow">{link.owner?.email ?? "UNKNOWN"} · {date(link.createdAt, true)}</span>
                  <strong>/{link.slug}</strong>
                  <span>{link.targetHost}</span>
                  <Tag tone="accent">待审</Tag>
                </button>
              ))}
            </div>
          ) : <Empty title="队列已清空" body="没有等待审核的链接。新请求会按创建时间进入这里。" />}
          {selected ? (
            <div className="review-decision">
              <span className="eyebrow">SELECTED · /{selected.slug}</span>
              <a href={selected.targetUrl} target="_blank" rel="noreferrer">{selected.targetUrl}</a>
              <Field label="审核备注 · NOTE" hint="拒绝时建议说明原因；通过时备注可留空。">
                <textarea className="input textarea" value={note} onChange={(event) => setNote(event.target.value)} placeholder="域名用途、风险判断或后续说明" />
              </Field>
              <div className="review-decision__actions">
                <Button variant="primary" onClick={() => reviewMutation.mutate("approve")} disabled={reviewMutation.isPending}><Check size={16} />通过并生效</Button>
                <Button variant="danger" onClick={() => reviewMutation.mutate("reject")} disabled={reviewMutation.isPending}><X size={16} />拒绝</Button>
              </div>
            </div>
          ) : null}
          {reviewQuery.data ? <footer className="pagination"><span>第 {reviewQuery.data.pagination.page} / {reviewQuery.data.pagination.totalPages} 页 · 共 {reviewQuery.data.pagination.total} 条待审</span><div><Button disabled={reviewPage <= 1} onClick={() => { setSelectedId(null); setReviewPage((value) => value - 1); }}>← 上一页</Button><Button disabled={reviewPage >= reviewQuery.data.pagination.totalPages} onClick={() => { setSelectedId(null); setReviewPage((value) => value + 1); }}>下一页 →</Button></div></footer> : null}
        </div>
        <div className="rules-panel">
          <header className="panel-heading"><div><span className="eyebrow">DOMAIN POLICY · REUSABLE</span><h2>域名规则</h2></div><Ban size={20} /></header>
          <form
            className="rule-form"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              createRuleMutation.mutate();
            }}
          >
            <Field label="域名模式 · PATTERN" hint="使用 *.example.com 匹配子域，不包含根域本身。">
              <input className="input" required value={pattern} onChange={(event) => setPattern(event.target.value)} placeholder="example.com 或 *.example.com" />
            </Field>
            <div className="segments segments--stretch">
              <button className={action === "allow" ? "segment segment--active" : "segment"} type="button" onClick={() => setAction("allow")}><Check size={13} />白名单</button>
              <button className={action === "block" ? "segment segment--active" : "segment"} type="button" onClick={() => setAction("block")}><Ban size={13} />拦截</button>
            </div>
            <Field label="备注 · NOTE"><input className="input" value={ruleNote} onChange={(event) => setRuleNote(event.target.value)} placeholder="说明用途或来源" /></Field>
            <Button variant="primary" type="submit" disabled={createRuleMutation.isPending}><Plus size={15} />添加规则</Button>
          </form>
          <div className="rule-list">
            {rulesQuery.isLoading ? <Loading /> : rulesQuery.error ? <ErrorState error={rulesQuery.error} /> : rulesQuery.data?.items.length ? rulesQuery.data.items.map((rule) => (
              <div className="rule-row" key={rule.id}>
                <div><Tag tone={rule.action === "allow" ? "neutral" : "accent"}>{rule.action === "allow" ? "白名单" : "拦截"}</Tag><strong>{rule.pattern}</strong><span>{rule.note || "无备注"}</span></div>
                <button className="icon-button icon-button--danger" type="button" onClick={() => deleteRuleMutation.mutate(rule.id)} title="删除规则"><Trash2 size={14} /></button>
              </div>
            )) : <Empty title="还没有域名规则" body="没有规则时，普通用户的目标默认进入待审。" />}
          </div>
          {rulesQuery.data ? <footer className="pagination"><span>第 {rulesQuery.data.pagination.page} / {rulesQuery.data.pagination.totalPages} 页 · 共 {rulesQuery.data.pagination.total} 条规则</span><div><Button disabled={rulePage <= 1} onClick={() => setRulePage((value) => value - 1)}>← 上一页</Button><Button disabled={rulePage >= rulesQuery.data.pagination.totalPages} onClick={() => setRulePage((value) => value + 1)}>下一页 →</Button></div></footer> : null}
          <footer className="rules-panel__footnote"><span className="eyebrow">ORDER</span> 拦截规则优先；同类型优先匹配更具体的域名。</footer>
        </div>
      </section>
    </div>
  );
}
